import { useState } from 'react';
import { ErreurApi } from '../api/client';
import { useRetirerColis } from '../api/hooks';
import type { CommandeListee } from '../api/types';
import { formatCourt } from '../format';
import type { ModalesCommande } from './actions-commande';
import { useToasts } from './Toasts';

/**
 * Les colis partis en plus du premier, dans le suivi de la fiche.
 *
 * Chacun a son lien de suivi, son statut relevé chez le transporteur et son
 * mail au client. La commande n'est livrée qu'une fois TOUS les colis arrivés,
 * premier compris — « Relever maintenant » les relève tous ensemble.
 */
export function ColisSupplementaires({
  commande, modales,
}: {
  commande: CommandeListee;
  modales: ModalesCommande;
}) {
  const { ajouter } = useToasts();
  const retirer = useRetirerColis();
  // Retirer se confirme d'un second clic : pas de boîte de dialogue du navigateur.
  const [aRetirer, setARetirer] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  async function confirmerRetrait(tracking: string) {
    setErreur(null);
    try {
      await retirer.mutateAsync({ id: commande.id, tracking });
      ajouter(`Colis ${tracking} retiré de ${commande.ref}.`);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Le retrait du colis a échoué.');
    }
    setARetirer(null);
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <h4 style={{ margin: 0, fontSize: 12.5 }}>
          {commande.colis.length ? `Autres colis (${commande.colis.length})` : 'Autres colis'}
        </h4>
        <button type="button" className="btn sm" onClick={() => modales.onAjoutColis(commande)}>
          Ajouter un colis
        </button>
      </div>

      {commande.colis.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
          {commande.colis.map((c) => {
            const dernier = c.mails[c.mails.length - 1];
            return (
              <li
                key={c.tracking}
                style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid var(--line)' }}
              >
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                  <span className="mono">{c.tracking}</span>
                  {c.transporteur && <> · {c.transporteur}</>}
                  <br />
                  <span style={{ fontSize: 11.5, color: c.livre ? 'var(--pine)' : 'var(--ink-2)' }}>
                    {c.statutTransporteur
                      ? `${c.statutTransporteur}${c.dateStatutTransporteur ? ` — ${formatCourt(c.dateStatutTransporteur)}` : ''}`
                      : 'Aucun point de suivi'}
                  </span>
                  {c.releveErreur && (
                    <span style={{ fontSize: 11.5, color: 'var(--brick)' }}> · relevé : {c.releveErreur}</span>
                  )}
                  <br />
                  <span
                    style={{
                      fontSize: 11.5,
                      color: c.mailEtat === 'echec' || c.mailEtat === 'a_envoyer' ? 'var(--brick)' : 'var(--ink-3)',
                    }}
                  >
                    {c.mailEtat === 'envoye' && dernier
                      ? `Mail envoyé${dernier.envoyeLe ? ` le ${formatCourt(dernier.envoyeLe.slice(0, 10))}` : ''}`
                        + (c.mails.length > 1 ? ` (${c.mails.length} envois)` : '')
                        + (dernier.simule ? ' — simulé, Outlook en mode local' : '')
                      : c.mailEtat === 'echec' ? `Mail non parti : ${c.mailErreur ?? 'cause inconnue'}`
                      : c.mailEtat === 'ignore' ? 'Mail écarté : le client ne sera pas prévenu.'
                      : 'Le client n’a pas été prévenu de ce colis.'}
                  </span>
                </span>
                {c.suivi && (
                  <a href={c.suivi} target="_blank" rel="noopener noreferrer" className="btn sm">
                    Suivi ↗
                  </a>
                )}
                <button
                  type="button" className={dernier ? 'btn sm' : 'btn pri sm'}
                  onClick={() => modales.onMailClient(commande, 'tracking', c.tracking)}
                >
                  {dernier ? 'Refaire le mail' : 'Envoyer le mail'}
                </button>
                {aRetirer === c.tracking ? (
                  <button
                    type="button" className="btn sm" disabled={retirer.isPending}
                    onClick={() => void confirmerRetrait(c.tracking)}
                  >
                    Confirmer le retrait
                  </button>
                ) : (
                  <button type="button" className="btn sm" onClick={() => setARetirer(c.tracking)}>
                    Retirer
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {erreur && <p className="err" role="alert" style={{ marginTop: 8 }}>{erreur}</p>}
    </div>
  );
}
